import React, { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import AppLayout from "../../layouts/AppLayout";
import PageNotFound from "../../pages/PageNotFound";
import MyDashboard from "../../pages/MyDashboard";
import { useSelector } from "react-redux";
import { authSelector } from "../../store";
import LoadingTdmTable from "../../components/UI/loading/LoadingTdmTable";
import LoadingAdministration from "../../components/UI/loading/Administration/LoadingAdministration";
import LoadingSettings from "../../components/UI/loading/Settings/LoadingSettings";
import LoadingVendor from "../../components/UI/loading/Vendor/LoadingVendor";
import RequireSubscription from "../RequireSubscription";
import { HasPermission } from "../../components/Permission/HasPermission";
import LoadingSherpaProjectsPage from "../../components/UI/loading/PageSkeletonLoading/LoadingSherpaProjectsPage";
import Unauthorized from "../../components/Unauthorized";
// import { MyApprovals } from "../../pages/MyApprovals";
import { ApprovalDetails } from "../../pages/MyApprovals/ApprovalDetails";
import LoadingCompanyDashboard from "../../components/UI/loading/LoadingCompanyDashboard";
import Learn from "../../pages/Learn";

const Clients = React.lazy(() => import("../../pages/SageAdmin/Clients"));
const ClientDetails = React.lazy(
  () => import("../../pages/SageAdmin/Clients/Details")
);
const MyTasks = React.lazy(() => import("../../pages/MyTasks"));
const Task = React.lazy(() => import("../../pages/MyTasks/Task"));
const MyDeliverables = React.lazy(() => import("../../pages/MyDeliverables"));
const Deliverable = React.lazy(
  () => import("../../pages/MyDeliverables/Deliverable")
);
const MyMilestones = React.lazy(() => import("../../pages/MyMilestones"));
const Milestone = React.lazy(
  () => import("../../pages/MyMilestones/Milestone")
);

const MyApprovals = React.lazy(() => import("../../pages/MyApprovals"));
const SherpaRoute = React.lazy(() => import("./SherpaRoute"));
const RoverRoute = React.lazy(() => import("./RoverRoute"));
const Sabacus = React.lazy(() => import("../../pages/Sabacus"));
const AdministrationRoute = React.lazy(() => import("./AdministrationRoute"));
const Vendor = React.lazy(() => import("../../pages/Vendor"));
const SettingsRoute = React.lazy(() => import("./SettingsRoute"));
const CompanyDashboard = React.lazy(
  () => import("../../pages/CompanyDashboard")
);

export default function AppRoute() {
  const { data } = useSelector(authSelector);

  return (
    <Routes>
      <Route path="/" element={<AppLayout />}>
        <Route index element={<Navigate to="dashboard" />} />

        {data.account !== "sageadmin" && (
          <Route
            path="company"
            element={
              <Suspense fallback={<LoadingCompanyDashboard />}>
                <CompanyDashboard />
              </Suspense>
            }
          />
        )}

        {data.account !== "sageadmin" && (
          <Route path="dashboard">
            <Route index element={<MyDashboard />} />
            <Route path="tasks">
              <Route
                index
                element={
                  <Suspense fallback={<LoadingTdmTable />}>
                    <MyTasks />
                  </Suspense>
                }
              />
              <Route path=":taskId" element={<Task />} />
            </Route>
            <Route path="deliverables">
              <Route
                index
                element={
                  <Suspense fallback={<LoadingTdmTable />}>
                    <MyDeliverables />
                  </Suspense>
                }
              />
              <Route path=":deliverableId" element={<Deliverable />} />
            </Route>
            <Route path="milestones">
              <Route
                index
                element={
                  <Suspense fallback={<LoadingTdmTable />}>
                    <MyMilestones />
                  </Suspense>
                }
              />
              <Route path=":milestoneId" element={<Milestone />} />
            </Route>
            <Route path="approvals">
              <Route
                index
                element={
                  <HasPermission
                    of="LIST_APPROVALS"
                    fallback={<Unauthorized />}
                  >
                    <MyApprovals />
                  </HasPermission>
                }
              />
              <Route
                path=":approvalId"
                element={
                  <HasPermission of="READ_APPROVAL" fallback={<Unauthorized />}>
                    <ApprovalDetails />
                  </HasPermission>
                }
              />
            </Route>
          </Route>
        )}
        {data.account !== "sageadmin" && (
          <Route
            path="sherpa/*"
            element={
              <RequireSubscription subscription="SHERPA">
                <Suspense fallback={<LoadingSherpaProjectsPage />}>
                  <SherpaRoute />
                </Suspense>
              </RequireSubscription>
            }
          />
        )}
        {data.account !== "sageadmin" && (
          <Route
            path="rover/*"
            element={
              <RequireSubscription subscription="ROVER">
                <RoverRoute />
              </RequireSubscription>
            }
          />
        )}
        {data.account !== "sageadmin" && (
          <Route path="sabacus" element={<Sabacus />} />
        )}
        {data.account === "sageadmin" && (
          <Route path="clients">
            <Route
              index
              element={
                <HasPermission of="LIST_CLIENTS" fallback={<PageNotFound />}>
                  <Clients />
                </HasPermission>
              }
            />
            <Route
              path=":clientId"
              element={
                <HasPermission of="READ_CLIENT" fallback={<PageNotFound />}>
                  <ClientDetails />
                </HasPermission>
              }
            />
          </Route>
        )}
        <Route
          path="administration/*"
          element={
            <Suspense fallback={<LoadingAdministration />}>
              <AdministrationRoute />
            </Suspense>
          }
        />
        <Route
          path="vendor"
          element={
            <HasPermission of="LIST_VENDORS" fallback={<Unauthorized />}>
              <Suspense fallback={<LoadingVendor />}>
                <Vendor />
              </Suspense>
            </HasPermission>
          }
        />
        {/* <Route path="vendor/new-vendor" element={<NewVendorForm />} /> */}
        <Route
          path="settings/*"
          element={
            <Suspense fallback={<LoadingSettings />}>
              <SettingsRoute />
            </Suspense>
          }
        />
        <Route path="/learn" element={<Learn />} />
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </Routes>
  );
}
